import {rootState} from '@mintdata-wsp/common/state/RootState';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {R1C1Location, R1C1Range} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/R1C1';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';
import {computed, observable} from 'mobx';

export enum CommandType
{
    SHEET = 'sheet',
    PAGE = 'page'
}

export interface ICommandMenuItem
{
    type:CommandType,
    text:string;
    data:any;
}

export class CommandMenuState
{
    @observable
    public isMenuOpen:boolean = false;

    @observable
    public filterText:string = null;

    @observable
    public items:Array<ICommandMenuItem> = [];

    @observable
    public selectedItemIndex:number = 0;

    public prevFocusedElement:HTMLElement = null;

    @computed
    public get filteredItems():Array<ICommandMenuItem>
    {
        const {
            ui: {
                visualDesigner: {
                    openDesign: {
                        designState: {
                            surface: {pages},
                            spreadsheets: {workbooks}
                        }
                    }
                }
            }
        } = rootState;
        const filterText:string = this.filterText
            ? this.filterText.replace(CommandMenuState.CELL_REF_FILTER_REGEX, '')
            : null;
        const match:RegExpMatchArray = this.filterText ? this.filterText.match(CommandMenuState.CELL_REF_COLUMN_ROW_REGEX) : null;
        const colIndexFrom:number = match && match[1] ? MeshLocationUtils.getColumnIndexByLetter(match[1]) : null;
        const rowIndexFrom:number = match && match[2] ? MeshLocationUtils.getRowIndexByLetter(match[2]) : null;
        const colIndexTo:number = match && match[3] ? MeshLocationUtils.getColumnIndexByLetter(match[3]) : null;
        const rowIndexTo:number = match && match[4] ? MeshLocationUtils.getRowIndexByLetter(match[4]) : null;

        const items:Array<ICommandMenuItem> = this.items.filter(item => item.text.includes(filterText));

        pages.forEach((page:Page) =>
        {
            if( !filterText || page.name.toLowerCase().includes(filterText.toLowerCase()) )
            {
                items.push({
                    type: CommandType.PAGE,
                    text: page.name,
                    data: {
                        pageId: page.id
                    }
                });
            }
        });

        workbooks.forEach((workbook:Workbook) =>
        {
            workbook.runtime.sheets.forEach((sheet:AnySheet) =>
            {
                const meshState:MeshState = workbook.view.meshStates.get(sheet.id);

                if( !filterText || sheet.name.toLowerCase().includes(filterText.toLowerCase()) )
                {
                    const hasCol:boolean = (colIndexFrom == null || colIndexFrom < meshState.size.numCols)
                        && (colIndexTo == null || colIndexTo < meshState.size.numCols);
                    const hasRow:boolean = (rowIndexFrom == null || rowIndexFrom < meshState.size.numRows)
                        && (rowIndexTo == null || rowIndexTo < meshState.size.numRows);

                    const locationFrom:R1C1Location = new R1C1Location(rowIndexFrom, false, colIndexFrom, false);
                    const isRange:boolean = colIndexTo != null || rowIndexTo != null;
                    const cellRefText:string = hasCol && hasRow
                        ? (isRange ? new R1C1Range(
                            locationFrom,
                            new R1C1Location(rowIndexTo, false, colIndexTo, false)
                        ) : locationFrom).toA1String(0, 0)
                        : null;

                    if( hasCol && hasRow )
                        items.push({
                            type: CommandType.SHEET,
                            text: sheet.name,
                            data: {
                                sheetId: sheet.id,
                                cellRefText,
                                colIndexFrom,
                                rowIndexFrom,
                                colIndexTo,
                                rowIndexTo
                            }
                        });
                }
            });
        });

        return items;
    }

    @computed
    public get selectedItem():ICommandMenuItem
    {
        return this.filteredItems[this.selectedItemIndex] || null;
    }

    public static CELL_REF_FILTER_REGEX = /(?:.)!([a-zA-Z]+)?([0-9]+)?(?::([a-zA-Z]+)?([0-9]+)?)?$/;
    public static CELL_REF_COLUMN_ROW_REGEX = /(?:.!)([a-zA-Z]+)?([0-9]+)?(?::([a-zA-Z]+)?([0-9]+)?)?$/;
}
